import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { WA, resolveMediaUrl } from "@/lib/api";
import { useProductMenu } from "@/hooks/useProductMenu";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.3a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.54a8.19 8.19 0 0 0 4.76 1.52V6.7a4.85 4.85 0 0 1-1-.01z" />
  </svg>
);

const socialIconClass =
  "w-10 h-10 rounded-full bg-white/10 hover:bg-[#E82020] text-white flex items-center justify-center transition";

const Footer = () => {
  const { settings } = useSettings();
  const { t, lang } = useLanguage();
  const s = settings || {};
  const wa = WA(s.whatsapp_number, s.whatsapp_template);
  const social = s.social || {};

  const products = useProductMenu(lang);

  const help = [
    { to: "/tentang", label: t("footer.aboutUs") },
    { to: "/layanan", label: t("nav.services") },
    { to: "/order-sample", label: t("nav.orderSample") },
    { to: "/karir", label: t("nav.career") },
    { to: "/live", label: t("nav.live") },
    { to: "/galeri", label: t("nav.gallery") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/kontak", label: t("nav.contact") },
  ];

  const socials = [
    { key: "instagram", href: social.instagram, label: "Instagram", Icon: Instagram },
    { key: "facebook", href: social.facebook, label: "Facebook", Icon: Facebook },
    { key: "youtube", href: social.youtube, label: "YouTube", Icon: Youtube },
    { key: "tiktok", href: social.tiktok, label: "TikTok", Icon: TikTokIcon },
  ].filter((x) => x.href);

  return (
    <footer className="sas-foot" data-testid="site-footer">
      <div className="sas-wrap grid md:grid-cols-4 gap-10">
        <div>
          {s.logo ? (
            <img src={resolveMediaUrl(s.logo)} alt={s.site_name || "Logo"} className="h-14 w-auto object-contain object-left mb-4 bg-white p-2 rounded-md" />
          ) : (
            <h4>PT Mandala Cahaya Sentosa</h4>
          )}
          <p>{s.tagline || t("footer.taglineFallback")}</p>
          <p className="mt-3">{t("footer.servicesLine")}</p>
          {socials.length > 0 && (
            <div className="flex flex-wrap gap-2.5 mt-5">
              {socials.map(({ key, href, label, Icon }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  title={label}
                  className={socialIconClass}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          )}
        </div>
        <div>
          <h4>{t("nav.products")}</h4>
          <ul>
            {products.map((p) => (
              <li key={p.label}><Link to={p.to}>{p.label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4>{t("footer.help")}</h4>
          <ul>
            {help.map((p) => (
              <li key={p.to}><Link to={p.to}>{p.label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4>{t("footer.contact")}</h4>
          <ul>
            {s.phone ? <li className="flex gap-2 items-start"><Phone size={15} className="mt-1" /> <a href={`tel:${String(s.phone).replace(/\s/g, "")}`}>{s.phone}</a></li> : null}
            {s.phone ? <li className="flex gap-2 items-start"><Phone size={15} className="mt-1" /> <a href={wa}>{t("footer.whatsappUs")}</a></li> : null}
            {s.email ? <li className="flex gap-2 items-start"><Mail size={15} className="mt-1" /> <a href={`mailto:${s.email}`}>{s.email}</a></li> : null}
            {s.address ? <li className="flex gap-2 items-start"><MapPin size={15} className="mt-1 shrink-0" /> {s.address}</li> : null}
          </ul>
        </div>
      </div>
      <div className="sas-wrap sas-copy">
        Copyright © {new Date().getFullYear()} PT Mandala Cahaya Sentosa. {t("footer.rights")}
      </div>
    </footer>
  );
};

export default Footer;
